import { useState, useRef, useEffect, useCallback, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'

export interface TypeaheadOption {
  id: string
  label: string
  sublabel?: string
  icon?: string
}

interface TypeaheadSearchProps {
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onSelect?: (option: TypeaheadOption) => void
  queryKey: string[]
  queryFn: () => Promise<TypeaheadOption[]>
  renderOption?: (option: TypeaheadOption, isHighlighted: boolean) => ReactNode
  maxResults?: number
  minChars?: number
  debounceMs?: number
  className?: string
  disabled?: boolean
  showOnFocus?: boolean
}

// Simple fuzzy match function
function fuzzyMatch(text: string, pattern: string): boolean {
  if (!pattern) return true
  const lowerText = text.toLowerCase()
  const lowerPattern = pattern.toLowerCase()
  
  // Check if all characters in pattern appear in text in order
  let patternIdx = 0
  for (let i = 0; i < lowerText.length && patternIdx < lowerPattern.length; i++) {
    if (lowerText[i] === lowerPattern[patternIdx]) {
      patternIdx++
    }
  }
  
  // Also check for simple includes for better UX
  return patternIdx === lowerPattern.length || lowerText.includes(lowerPattern)
}

// Highlight matching characters
function highlightMatch(text: string, pattern: string): ReactNode {
  if (!pattern) return text
  
  const lowerText = text.toLowerCase()
  const lowerPattern = pattern.toLowerCase()
  const idx = lowerText.indexOf(lowerPattern)
  
  if (idx === -1) return text
  
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-primary-600 dark:text-primary-400">
        {text.slice(idx, idx + pattern.length)}
      </span>
      {text.slice(idx + pattern.length)}
    </>
  )
}

export default function TypeaheadSearch({
  placeholder = 'Search...',
  value,
  onChange,
  onSelect,
  queryKey,
  queryFn,
  renderOption,
  maxResults = 8,
  minChars = 0,
  debounceMs = 150,
  className,
  disabled = false,
  showOnFocus = true,
}: TypeaheadSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [debouncedValue, setDebouncedValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Debounce input value
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), debounceMs)
    return () => clearTimeout(timer)
  }, [value, debounceMs])

  // Fetch data
  const { data: options = [], isLoading } = useQuery({
    queryKey: [...queryKey, 'typeahead'],
    queryFn,
    staleTime: 30000, // Cache for 30 seconds
  })

  // Filter options based on input
  const filteredOptions = options
    .filter((opt) => 
      fuzzyMatch(opt.label, debouncedValue) || 
      (opt.sublabel && fuzzyMatch(opt.sublabel, debouncedValue))
    )
    .slice(0, maxResults)

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
        return
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }, [isOpen, highlightedIndex, filteredOptions])

  const handleSelect = (option: TypeaheadOption) => {
    onChange(option.label)
    onSelect?.(option)
    setIsOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()
  }

  const handleFocus = () => {
    if (showOnFocus || value.length >= minChars) {
      setIsOpen(true)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    if (newValue.length >= minChars || showOnFocus) {
      setIsOpen(true)
    }
    setHighlightedIndex(-1)
  }

  const handleClear = () => {
    onChange('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlighted = dropdownRef.current.children[highlightedIndex] as HTMLElement
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  const showDropdown = isOpen && (filteredOptions.length > 0 || isLoading)

  const defaultRenderOption = (option: TypeaheadOption, isHighlighted: boolean) => (
    <div
      className={clsx(
        'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
        isHighlighted 
          ? 'bg-primary-50 dark:bg-primary-900/30' 
          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
      )}
    >
      {option.icon && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center">
          <i className={`fas ${option.icon} text-gray-500 dark:text-gray-400 text-sm`}></i>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {highlightMatch(option.label, debouncedValue)}
        </p>
        {option.sublabel && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {highlightMatch(option.sublabel, debouncedValue)}
          </p>
        )}
      </div>
    </div>
  )

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Input with icon */}
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center">
          {isLoading ? (
            <i className="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
          ) : (
            <i className="fas fa-search text-gray-400 text-sm"></i>
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            'w-full pl-11 pr-10 py-2.5 rounded-lg border transition-all',
            'bg-white dark:bg-gray-700 text-gray-900 dark:text-white',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            disabled 
              ? 'border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60' 
              : 'border-gray-300 dark:border-gray-600'
          )}
          autoComplete="off"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          role="combobox"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-auto"
          role="listbox"
        >
          {isLoading ? (
            <div className="px-4 py-6 text-center">
              <i className="fas fa-spinner fa-spin text-gray-400 text-lg"></i>
              <p className="text-sm text-gray-500 mt-2">Loading...</p>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <i className="fas fa-search text-gray-300 dark:text-gray-600 text-2xl"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                No results found for "{debouncedValue}"
              </p>
            </div>
          ) : (
            <>
              {filteredOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={highlightedIndex === index}
                >
                  {renderOption 
                    ? renderOption(option, highlightedIndex === index) 
                    : defaultRenderOption(option, highlightedIndex === index)
                  }
                </div>
              ))}
              {filteredOptions.length >= maxResults && (
                <div className="px-4 py-2 text-xs text-center text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700">
                  Type to narrow results...
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
